import React, { useState, useEffect } from 'react';
import { useGetFilesQuery } from '../../app/services/fileService';
import { Image, PlayCircle, FileText } from 'lucide-react';

interface MediaPreviewProps {
    fileName: string;
    type: 'image' | 'video' | 'file';
    className?: string;
    style?: React.CSSProperties;
}

const MediaPreview: React.FC<MediaPreviewProps> = ({ fileName, type, className, style }) => {
    const [mediaUrl, setMediaUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const {
        data: fileBlob,
        isLoading: isFetching,
        isError: fetchError
    } = useGetFilesQuery({ fileName }, {
        skip: !fileName
    });

    useEffect(() => {
        if (fileBlob) {
            try {
                const url = URL.createObjectURL(fileBlob);
                setMediaUrl(url);
                setIsLoading(false);
                setHasError(false);

                return () => {
                    URL.revokeObjectURL(url);
                };
            } catch (error) {
                console.error('Error creating object URL:', error);
                setHasError(true);
                setIsLoading(false);
            }
        } else if (fetchError) {
            setHasError(true);
            setIsLoading(false);
        }
    }, [fileBlob, fetchError]);

    if (isLoading || isFetching) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
            </div>
        );
    }

    if (hasError || !mediaUrl) {
        return (
            <div className="fallback-content w-full h-full flex flex-col items-center justify-center gap-1 text-gray-500">
                {type === 'image' ? (
                    <Image className="h-6 w-6" />
                ) : type === 'video' ? (
                    <PlayCircle className="h-6 w-6" />
                ) : (
                    <FileText className="h-6 w-6" />
                )}
                <span className="text-xs">
                    {type === 'image' ? 'Image' : type === 'video' ? 'Video' : 'File'}
                </span>
            </div>
        );
    }

    if (type === 'image') {
        return (
            <img
                src={mediaUrl}
                alt="Component content"
                className={className}
                style={style}
                onError={() => setHasError(true)}
            />
        );
    } else if (type === 'video') {
        return (
            <video
                src={mediaUrl}
                className={className}
                style={style}
                autoPlay
                muted
                loop
                onError={() => setHasError(true)}
            />
        );
    } else {
        const fileExtension = fileName.toLowerCase().split('.').pop() || '';
        const isPDF = fileExtension === 'pdf';
        const isTextFile = ['txt', 'csv'].includes(fileExtension);

        if (isPDF || isTextFile) {
            return (
                <embed
                    src={mediaUrl}
                    type={fileBlob?.type || 'application/pdf'}
                    className={className}
                    style={{
                        ...style,
                        width: '100%',
                        height: '100%'
                    }}
                    title="Document preview"
                />
            );
        }

        return (
            <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-4 bg-gray-50">
                <FileText className="h-12 w-12 text-gray-400" />
                <div className="text-center">
                    <p className="text-sm font-medium text-gray-700 mb-2">Document Preview</p>
                    <p className="text-xs text-gray-500 mb-4">{fileName}</p>
                    <p className="text-xs text-gray-400 mb-4">
                        {fileExtension.toUpperCase()} files will be displayed on the actual screen
                    </p>
                    <a
                        href={mediaUrl}
                        download={fileName}
                        className="inline-flex items-center px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                        onClick={(e) => e.stopPropagation()}
                    >
                        Download to View
                    </a>
                </div>
            </div>
        );
    }
};

export default MediaPreview;
